import React, { useEffect, useState } from 'react';
import { Input, InputNumber, Switch } from 'antd';
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { Cron } from 'react-js-cron';
import cronstrue from 'cronstrue';
import 'react-js-cron/dist/styles.css';
import Field from './Field';

/* -------------------------------------------------------------------------- */
/*  Toggle                                                                    */
/* -------------------------------------------------------------------------- */

type SwitchRowProps = {
    label: string;
    /** injected by Form.Item via valuePropName="checked" */
    checked?: boolean;
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
};

/**
 * The setting, then the switch that answers it — read left to right and
 * answered on the right, the way a settings list reads. No box: a boolean is
 * one line, and a border around it only spends room saying so.
 */
const SwitchRow = ({ label, checked, onChange, disabled, ...rest }: SwitchRowProps) => (
    <div className={`nf-switch${checked ? ' nf-switch--on' : ''}${disabled ? ' nf-switch--off' : ''}`}>
        {/* pointer convenience only — the switch itself stays the keyboard target */}
        <span className="nf-switch__text" onClick={() => !disabled && onChange?.(!checked)}>
            {label}
        </span>
        <Switch checked={checked} onChange={onChange} disabled={disabled} aria-label={label} {...rest} />
    </div>
);

export type ToggleFieldProps = {
    name: string | (string | number)[];
    /** the switch names itself on its own line */
    label: string;
    hint?: React.ReactNode;
    span?: number;
    /** extra classes on the field — e.g. to drop the empty label slot below */
    className?: string;
};

/**
 * A boolean, named on the line it sits on. Sharing a row with a labelled input
 * would leave the two controls on different lines, so a toggle narrower than
 * the full width keeps an empty label slot to line them back up.
 *
 * A row of nothing but toggles has nothing to line up with, and that slot is
 * then a blank line above them — pass `nf-field--unlabelled` to drop it.
 */
export const ToggleField = ({ name, label, hint, span = 12, className }: ToggleFieldProps) => (
    <Field
        name={name}
        span={span}
        valuePropName="checked"
        label={span < 12 ? ' ' : undefined}
        hint={hint}
        className={[span < 12 ? 'nf-field--aligned' : '', className || ''].filter(Boolean).join(' ') || undefined}
    >
        <SwitchRow label={label} />
    </Field>
);

/* -------------------------------------------------------------------------- */
/*  Duration (stored as whole seconds)                                        */
/* -------------------------------------------------------------------------- */

export const humanizeSeconds = (total?: number | null) => {
    if (total === undefined || total === null || Number.isNaN(total)) return '';
    if (total < 60) return `${total} second${total === 1 ? '' : 's'}`;
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    const minPart = `${mins} minute${mins === 1 ? '' : 's'}`;
    return secs ? `${minPart} ${secs}s` : minPart;
};

type DurationInputProps = {
    value?: number | null;
    onChange?: (value: number | null) => void;
    disabled?: boolean;
    min?: number;
    max?: number;
};

/**
 * Seconds as a plain number. The previous mm:ss time picker made a duration
 * look like a time of day and could not express "90 seconds" without maths.
 */
export const DurationInput = ({ value, onChange, disabled, min = 5, max = 3600, ...rest }: DurationInputProps) => (
    <span className="nf-duration">
        <InputNumber
            value={value ?? undefined}
            onChange={(next) => onChange?.(next as number | null)}
            min={min}
            max={max}
            step={5}
            disabled={disabled}
            suffix="sec"
            {...rest}
        />
        {typeof value === 'number' && value >= 60 && (
            <span className="nf-duration__hint">= {humanizeSeconds(value)}</span>
        )}
    </span>
);

/* -------------------------------------------------------------------------- */
/*  Cron expression                                                           */
/* -------------------------------------------------------------------------- */

export const describeCron = (expression?: string | null) => {
    if (!expression) return { ok: false, text: 'No schedule set' };
    try {
        return { ok: true, text: cronstrue.toString(expression, { locale: 'en' }) };
    } catch (e) {
        return { ok: false, text: 'Not a valid cron expression' };
    }
};

type CronInputProps = {
    value?: string | null;
    onChange?: (value: string) => void;
    disabled?: boolean;
};

/**
 * A cron expression, editable either by picking from the builder or by typing
 * the expression directly. Both edit the same value, and the plain-English
 * reading of it is always on screen.
 */
export const CronInput = ({ value, onChange, disabled, ...rest }: CronInputProps) => {
    const [draft, setDraft] = useState(value || '0 * * * *');

    // adopt values set from outside (record load, reset) without fighting typing
    useEffect(() => {
        if (value && value !== draft) setDraft(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const commit = (next: string) => {
        setDraft(next);
        // The builder re-emits the expression it was handed as it mounts and
        // normalises. Passing that on would touch the form for no change —
        // enough to mark it dirty, and enough to supersede a validation
        // already in flight, which silently drops the save that started it.
        if (next !== value) onChange?.(next);
    };

    const described = describeCron(draft);

    return (
        <div className="nf-cron">
            <Input
                className="nf-mono"
                value={draft}
                onChange={(e) => commit(e.target.value)}
                placeholder="*/15 * * * *"
                disabled={disabled}
                style={{ marginBottom: 12 }}
                {...rest}
            />
            <Cron value={draft} setValue={commit} clearButton={false} disabled={disabled} />
            <div className={`nf-cron__preview${described.ok ? '' : ' nf-cron__preview--bad'}`}>
                {described.ok ? <CheckCircleFilled aria-hidden="true" /> : <WarningFilled aria-hidden="true" />}
                <span>{described.text}</span>
            </div>
        </div>
    );
};
